import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useParams } from 'react-router-dom';
import useSWR from 'swr';
import { toast } from 'sonner';
import {
  ArrowLeft, CircleCheck, ExternalLink, GraduationCap, Pin, Send, ShieldAlert,
} from 'lucide-react';
import { get, post, put, ApiError } from '@/lib/api';
import { restampNotice, type SiblingOptionsResult } from '@/lib/sibling-restamp-notice';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { PageHeader } from '@/components/layout/PageHeader';
import { Thumbnail } from '@/components/domain/Thumbnail';
import { LiveArtBanner } from '@/components/domain/LiveArtBanner';
import {
  LogoCard,
  StoreNameNumberFontCard,
  type ShopifyProductRow,
  type TeamStoreDetail,
} from '@/components/domain/StoreEditorDrawer';
import { formatRelative } from '@/lib/time';
import { BOTH_GENDER_NOTE, STORE_GENDERS, genderLabel, isBothGender, resolveStoreGender } from '@/lib/store-gender';
import { PayoutTable } from '@/routes/Payouts';
import type { PayoutRow } from '@/lib/payouts';
import { coachOpenHref } from '@/lib/coach';
import { DropEmailForm } from '@/components/domain/DropEmailForm';
import { ChangeRequestList, FIELD_LABELS, type ChangeRequest, type ChangeRequestType } from '@/components/domain/ChangeRequestList';
import {
  isLogoOnlyArtwork,
  lineupCoverage,
  sortArtworkTemplates,
  sortProductTemplates,
  sportToArtworkTemplateId,
  type TemplateLike,
} from '@/lib/store-template-kinds';
import { productLineupHref, templateEditorHref } from '@/lib/template-editor-query';
import { PACKS_EDITABLE, packsSelectState } from '@/lib/player-packs-choice';
import { PERSONALIZATION_MODE_OPTIONS, modeSelectState, storefrontNotUpdated, type ModeSaveResult } from '@/lib/personalization-mode';
import {
  artworkAllowedForProduct,
  artworkOptionsForProduct,
  currentArtworkSelection,
  currentFrameSelection,
  currentProductSelection,
  suggestArtworkForProduct,
  templatesPatchFromSelection,
} from '@/lib/store-artwork-patch';

const TABS = ['Overview', 'Catalog', 'Artwork', 'Template', 'Requests', 'Personalization', 'Orders', 'Buyers', 'Payouts', 'Leading Edge'] as const;
type Tab = (typeof TABS)[number];

const STATUS_TONE: Record<string, string> = {
  pending_review: 'bg-bg text-muted',
  pending_artwork: 'bg-warning/10 text-warning',
  artwork_ready: 'bg-primary/10 text-primary',
  artwork_failed: 'bg-danger/10 text-danger',
  rendering_proof: 'bg-primary/10 text-primary',
  proof_sent: 'bg-success/10 text-success',
  proof_email_failed: 'bg-danger/10 text-danger',
  changes_requested: 'bg-warning/10 text-warning',
  approved: 'bg-warning/10 text-warning',
  creating_products: 'bg-primary/10 text-primary',
  live: 'bg-success/10 text-success',
  closing_soon: 'bg-warning/10 text-warning',
  shopify_error: 'bg-danger/10 text-danger',
  live_with_errors: 'bg-warning/10 text-warning',
  archived: 'bg-bg text-muted',
  archiving: 'bg-warning/10 text-warning',
  closed: 'bg-bg text-muted',
};

const ARCHIVED = new Set(['archived', 'archiving', 'archived_failed', 'closed']);
// Proof road (mirrors ghostfit-console/lib/team-store-proof-send.js): a send
// from a prep status, a resend after the customer already has one.
const PROOF_SENDABLE = new Set(['pending_review', 'artwork_ready', 'changes_requested']);
const PROOF_RESEND = new Set(['proof_sent', 'proof_email_failed']);
const PROOF_VIEWABLE = new Set(['rendering_proof', 'proof_sent', 'proof_email_failed', 'changes_requested', 'approved', 'creating_products', 'live', 'live_with_errors']);
// Override preflight only on stores that have not been built. Mirrors
// APPROVABLE_STATUSES in ghostfit-console/lib/teamstore-state.js, which the
// override-preflight route enforces (409 status_not_overridable otherwise).
const OVERRIDABLE = new Set(['pending_review', 'changes_requested', 'artwork_ready', 'processing_artwork', 'artwork_failed', 'proof_sent', 'proof_email_failed']);
const SELECT_CLASS = 'h-8 w-full rounded-sm border border-border bg-white px-2 text-[12px]';
const LIVE = new Set(['live', 'live_with_errors']);

function errMsg(e: unknown, fallback: string): string {
  const body = (e as ApiError).body as { message?: string; error?: string } | null;
  return body?.message || body?.error || fallback;
}

function storeUrl(s: TeamStoreDetail): string | null {
  return s.store_url || s.shopifyCollectionUrl || s.collectionUrl || s.storeUrl || null;
}

function storeHandle(s: TeamStoreDetail): string | null {
  return s.store_handle || s.shopifyCollectionHandle || null;
}

// A missing rate is a LEGACY doc, not a 10/7 doc. Painting 10/7 onto a store
// that never persisted a rate makes the page assert a giveback nobody agreed
// to — and at-cost stores really do give back 0. Same rule as admin v2 (#377).
function pct(rate?: number | null): string | null {
  if (rate == null || !Number.isFinite(rate)) return null;
  return String(rate <= 1 ? Math.round(rate * 100) : rate);
}

function commissionLabel(s: TeamStoreDetail): string {
  const school = pct(s.commissionRate);
  const rep = pct(s.salesRepCommissionRate);
  if (school == null && rep == null) return 'legacy';
  return `${school ?? 'legacy'} / ${rep ?? 'legacy'}`;
}

function money(n?: number): string {
  if (n == null || Number.isNaN(n)) return '—';
  return `$${Number(n).toFixed(2)}`;
}

function garmentKey(p: ShopifyProductRow): string {
  const id = String(p.productConfigId || '');
  const parts = id.split('-');
  if (parts.length >= 4) return parts[parts.length - 2];
  const title = String(p.title || '');
  const cut = title.split(/[—–-]/)[0]?.trim();
  return cut || 'Other';
}

type OrdersPayload = {
  success?: boolean;
  orders?: {
    id: string;
    shopifyOrderId?: string;
    createdAt?: string;
    subtotal?: number;
    totalQuantity?: number;
    customer?: { firstName?: string; lastName?: string; email?: string };
  }[];
  orderStats?: TeamStoreDetail['orderStats'];
};

type QuarterToDatePayload = {
  quarter?: string;
  quarterOrders?: number;
  quarterRevenue?: number;
  quarterUnits?: number;
};

export default function StorePage() {
  const { id } = useParams<{ id: string }>();
  const [tab, setTab] = useState<Tab>('Overview');
  const key = id ? `/api/active-stores/${id}` : null;
  const { data: store, isLoading, mutate: refresh } = useSWR<TeamStoreDetail>(key, get);

  if (!id) return null;
  if (isLoading || !store) {
    return (
      <div className="px-7 py-6">
        <Skeleton className="mb-4 h-8 w-64" />
        <Skeleton className="h-40 w-full rounded-[14px]" />
      </div>
    );
  }

  const url = storeUrl(store);
  const handle = storeHandle(store);
  const tone = STATUS_TONE[store.status || ''] || 'bg-bg text-muted';

  return (
    <div className="px-7 py-6">
      <PageHeader
        title={store.schoolName || store.fundraiserName || store.id}
        subtitle={
          <span className="flex flex-wrap items-center gap-2">
            {store.storeNumberDisplay && <span className="font-mono">{store.storeNumberDisplay}</span>}
            {store.status && <Badge className={tone}>{store.status === 'closing_soon' ? 'ends soon' : store.status.replace(/_/g, ' ')}</Badge>}
            {store.sportType && <span className="capitalize">{store.sportType}</span>}
            {store.endDate ? store.endDate.slice(0, 10) : 'Open'}
          </span>
        }
        actions={
          <div className="flex items-center gap-2">
            <Button asChild variant="secondary" size="sm">
              <Link to="/active-stores"><ArrowLeft className="h-3.5 w-3.5" aria-hidden /> All stores</Link>
            </Button>
            <Button asChild variant="secondary" size="sm">
              <a href={coachOpenHref(store.id)}>
                <GraduationCap className="h-3.5 w-3.5" aria-hidden /> Coach view
              </a>
            </Button>
            {url && (
              <Button asChild size="sm">
                <a href={url} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="h-3.5 w-3.5" aria-hidden /> Open live store
                </a>
              </Button>
            )}
          </div>
        }
      />

      <div className="mb-5 flex flex-wrap gap-1.5">
        {TABS.map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={`rounded-full px-3 py-1 text-[11.5px] font-medium transition-colors ${tab === t ? 'bg-text text-white' : 'border border-black/[0.08] bg-surface text-muted hover:bg-bg'}`}
          >
            {t}
          </button>
        ))}
      </div>

      {tab === 'Overview' && <Overview store={store} url={url} handle={handle} refresh={async () => { await refresh(); }} />}
      {tab === 'Catalog' && <Catalog store={store} refresh={async () => { await refresh(); }} />}
      {tab === 'Artwork' && <Artwork store={store} refresh={async () => { await refresh(); }} />}
      {tab === 'Template' && <TemplateTab store={store} refresh={async () => { await refresh(); }} />}
      {tab === 'Requests' && <RequestsTab storeId={store.id} />}
      {tab === 'Personalization' && <PersonalizationTab store={store} refresh={async () => { await refresh(); }} />}
      {tab === 'Orders' && <OrdersTab store={store} />}
      {tab === 'Buyers' && <BuyersTab store={store} />}
      {tab === 'Payouts' && <PayoutsTab store={store} />}
      {tab === 'Leading Edge' && <LeadingEdgeTab store={store} />}
    </div>
  );
}

function Row({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div>
      <dt className="text-[11px] font-semibold uppercase tracking-[0.05em] text-muted">{label}</dt>
      <dd className="mt-0.5 text-[13px]">{children}</dd>
    </div>
  );
}

type CoachKitPayload = { texts?: { day1: string; week2: string } };

// New stores get this kit on their go-live email. This is the same kit on demand,
// for a store that went live earlier or a coach who lost the email.
function CoachKitCard({ storeId }: { storeId: string }) {
  const { data, error, isLoading } = useSWR<CoachKitPayload>(`/api/team-stores/${storeId}/coach-kit`, get, {
    shouldRetryOnError: false,
    revalidateOnFocus: false,
  });
  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copied');
    } catch {
      toast.error('Could not copy. Select the text instead.');
    }
  };
  const box = (label: string, text: string) => (
    <div key={label}>
      <div className="mb-1 flex items-center justify-between text-[11.5px] font-semibold">
        {label}
        <Button size="sm" variant="ghost" onClick={() => copy(text)}>Copy</Button>
      </div>
      <p className="whitespace-pre-wrap break-words rounded-[10px] bg-bg p-2.5 text-[11.5px]">{text}</p>
    </div>
  );
  return (
    <div className="rounded-[14px] border border-black/[0.05] bg-surface p-5 shadow-card">
      <h3 className="mb-3 text-[11px] font-semibold uppercase tracking-[0.05em] text-muted">Coach share kit</h3>
      <div className="mb-3 flex flex-wrap gap-3 text-[12px]">
        <a href={`/api/team-stores/${storeId}/coach-kit/flyer.pdf`} className="text-primary underline-offset-2 hover:underline">Flyer (PDF)</a>
        <a href={`/api/team-stores/${storeId}/coach-kit/share-guide.pdf`} className="text-primary underline-offset-2 hover:underline">One-page guide (PDF)</a>
      </div>
      {error && <p className="text-[12px] text-danger">Could not build the kit: {error.message}</p>}
      {isLoading && <p className="text-[12px] text-muted">Building the kit…</p>}
      {data?.texts && (
        <div className="space-y-3">
          {box('Day one text', data.texts.day1)}
          {box('One week later', data.texts.week2)}
        </div>
      )}
    </div>
  );
}

function Overview({ store, url, handle, refresh }: { store: TeamStoreDetail; url: string | null; handle: string | null; refresh: () => Promise<void> }) {
  const { data: qtd } = useSWR<QuarterToDatePayload>(
    `/api/team-stores/${store.id}/quarter-to-date`,
    get,
    { shouldRetryOnError: false },
  );
  const stats = {
    ...(store.orderStats || {}),
    ...(qtd && (qtd.quarterOrders != null || qtd.quarterRevenue != null || qtd.quarterUnits != null)
      ? {
          quarterOrders: qtd.quarterOrders,
          quarterRevenue: qtd.quarterRevenue,
          quarterUnits: qtd.quarterUnits,
        }
      : {}),
  };
  const hasQuarter = stats.quarterOrders != null || stats.quarterRevenue != null || stats.quarterUnits != null;
  const hasLifetime = stats.totalOrders != null || stats.totalRevenue != null || stats.totalUnits != null;
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <div className="rounded-[14px] border border-black/[0.05] bg-surface p-5 shadow-card">
        <h3 className="mb-3 text-[11px] font-semibold uppercase tracking-[0.05em] text-muted">Store</h3>
        <dl className="grid grid-cols-2 gap-x-6 gap-y-3">
          <Row label="School">{store.schoolName || '—'}</Row>
          <Row label="Sport"><span className="capitalize">{store.sportType || store.sport_id || '—'}</span></Row>
          <Row label="Gender">
            <span className="capitalize">{genderLabel(resolveStoreGender(store))}</span>
            {isBothGender(resolveStoreGender(store)) && (
              <p className="mt-1 text-[11px] text-muted">{BOTH_GENDER_NOTE}</p>
            )}
          </Row>
          <Row label="Mascot">{store.mascot || '—'}</Row>
          <Row label="Pin">
            {store.ghostfit_id ? (
              <span className="inline-flex items-center gap-1 font-mono text-[11.5px]">
                <Pin className="h-3 w-3 text-subtle" aria-hidden /> {store.ghostfit_id}
              </span>
            ) : '—'}
          </Row>
          <Row label="Live URL">
            {url ? (
              <a href={url} target="_blank" rel="noopener noreferrer" className="text-primary underline-offset-2 hover:underline">
                {handle || url}
              </a>
            ) : 'Not live yet'}
          </Row>
          <Row label="Handle"><span className="font-mono text-[11.5px]">{handle || '—'}</span></Row>
          <Row label="Dates">
            {store.startDate?.slice(0, 10) || '—'} → {store.endDate ? store.endDate.slice(0, 10) : 'Open'}
          </Row>
          <Row label="Commission">{commissionLabel(store)} <span className="text-[11px] text-muted">(school / rep)</span></Row>
          <Row label="Rep">{store.salesRepName || store.salesRepEmail || '—'}</Row>
          <Row label="Admin">{store.adminEmail || '—'}</Row>
          <Row label="Coach">{store.head_coach_name || store.head_coach_email || '—'}</Row>
          <Row label="Coach view">
            <a href={coachOpenHref(store.id)} className="text-primary underline-offset-2 hover:underline">Coach view (signed link)</a>
          </Row>
          {store.leading_edge_event_id && (
            <Row label="LE event"><span className="font-mono text-[11.5px]">{store.leading_edge_event_id}</span></Row>
          )}
          {store.source && <Row label="Source">{store.source}</Row>}
          {store.lastRolloutAt && (
            <Row label="Last rollout">{formatRelative(store.lastRolloutAt)}</Row>
          )}
        </dl>
        <div className="mt-4 flex items-center gap-2">
          {store.primaryColor && <span title={store.primaryColorName} className="h-5 w-5 rounded-full border border-black/10" style={{ background: store.primaryColor }} />}
          {store.secondaryColor && <span title={store.secondaryColorName} className="h-5 w-5 rounded-full border border-black/10" style={{ background: store.secondaryColor }} />}
          {store.tertiaryColor && <span title={store.tertiaryColorName} className="h-5 w-5 rounded-full border border-black/10" style={{ background: store.tertiaryColor }} />}
          <span className="text-[11.5px] text-muted">
            {[store.primaryColorName, store.secondaryColorName, store.tertiaryColorName].filter(Boolean).join(' · ') || 'No colors set'}
          </span>
        </div>
      </div>

      <div className="space-y-4">
        {url && <CoachKitCard storeId={store.id} />}
        <div className="rounded-[14px] border border-black/[0.05] bg-surface p-5 shadow-card">
          <h3 className="mb-3 text-[11px] font-semibold uppercase tracking-[0.05em] text-muted">Logos</h3>
          {(store.logos || []).length === 0 ? (
            <p className="text-[12px] text-muted">No logos on this store.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {(store.logos || []).map((l) => (
                <div key={l.id || l.gcsPath} className="flex items-center gap-2 rounded-[10px] bg-bg px-2 py-1.5">
                  <Thumbnail path={l.gcsPath} alt={l.label} className="h-9 w-9" />
                  <span className="text-[12px]">{l.label || 'Logo'}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {(hasQuarter || hasLifetime) && (
          <div className="rounded-[14px] border border-black/[0.05] bg-surface p-5 shadow-card">
            <h3 className="mb-3 text-[11px] font-semibold uppercase tracking-[0.05em] text-muted">
              {hasQuarter ? 'This quarter' : 'Lifetime'} orders
            </h3>
            <dl className="grid grid-cols-3 gap-3 text-[13px]">
              <Row label="Orders">{hasQuarter ? stats.quarterOrders ?? '—' : stats.totalOrders ?? '—'}</Row>
              <Row label="Units">{hasQuarter ? stats.quarterUnits ?? '—' : stats.totalUnits ?? '—'}</Row>
              <Row label="Revenue">{money(hasQuarter ? stats.quarterRevenue : stats.totalRevenue)}</Row>
            </dl>
            {hasQuarter && hasLifetime && (
              <p className="mt-3 text-[11px] text-muted">
                Lifetime: {stats.totalOrders ?? 0} orders · {stats.totalUnits ?? 0} units · {money(stats.totalRevenue)}
              </p>
            )}
          </div>
        )}
        <SeasonControls store={store} refresh={refresh} />
      </div>
    </div>
  );
}

function SeasonControls({ store, refresh }: { store: TeamStoreDetail; refresh: () => Promise<void> }) {
  const [busy, setBusy] = useState<string | null>(null);
  const archived = ARCHIVED.has(store.status || '');
  const live = LIVE.has(store.status || '');
  if (!archived && !live) return null;

  async function archiveSeason() {
    if (!confirm('Archive this season on Shopify? Products leave the storefront.')) return;
    setBusy('archive-season');
    try {
      await post(`/api/team-stores/${store.id}/archive-season`, {});
      toast.success('Season archive requested');
      await refresh();
    } catch (e) {
      toast.error(errMsg(e, 'Archive failed'));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="rounded-[14px] border border-black/[0.05] bg-surface p-5 shadow-card">
      <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-[0.05em] text-muted">Season</h3>
      {archived ? (
        // No Restore button: teamstore-createproducts-v4 has no /restore-season
        // route. Offering one would 502 on every click while implying the
        // failure was upstream. Un-drafting is a Shopify job done by hand today.
        <p className="text-[12px] text-muted">
          This store is {store.status?.replace(/_/g, ' ')}. Restoring a season has no endpoint yet —
          un-draft the products in Shopify, or rebuild the store.
        </p>
      ) : (
        <>
          <p className="mb-3 text-[12px] text-muted">Archive drafts the season on Shopify. The store record stays — this is not a quarterly payout.</p>
          <Button size="sm" variant="secondary" disabled={!!busy} onClick={() => void archiveSeason()}>
            {busy === 'archive-season' ? 'Archiving…' : 'Archive season'}
          </Button>
        </>
      )}
    </div>
  );
}

function Catalog({ store, refresh }: { store: TeamStoreDetail; refresh: () => Promise<void> }) {
  const [busy, setBusy] = useState<string | null>(null);
  const products = store.shopifyProductSummary || [];
  const live = store.status === 'live' || store.status === 'live_with_errors';
  const grouped = useMemo(() => {
    const m = new Map<string, ShopifyProductRow[]>();
    for (const p of products) {
      const k = garmentKey(p);
      m.set(k, [...(m.get(k) || []), p]);
    }
    return [...m.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [products]);

  // No "Preview missing" / "Add missing" here. Those post `computeDesired:true`
  // to v4 /create-missing, and the v4 on main rejects that body — it only
  // retries an explicit non-empty failedConfigIds. computeDesired arrives with
  // the fleet PR (#355). Until then a top-up button is a 400 with a
  // reassuring label, which is the worst possible thing on a live storefront.

  async function hideOrRestore(p: ShopifyProductRow, restore: boolean) {
    const productId = p.shopifyGid || p.shopifyProductId;
    if (!productId) return;
    const path = restore ? 'restore-product' : 'archive-product';
    setBusy(productId);
    try {
      const r = await post<{ result?: { siblingOptions?: SiblingOptionsResult } }>(
        `/api/team-stores/${store.id}/${path}`,
        { productId },
      );
      // Hiding a color is two writes — the status flip and the sibling swatch
      // re-stamp. restampNotice decides what the operator is told; see that
      // module for why a benign noop must stay quiet.
      const notice = restampNotice(r?.result?.siblingOptions, restore);
      if (notice.level === 'warning') toast.warning(notice.message, { description: notice.description });
      else toast.success(notice.message);
      await refresh();
    } catch (e) {
      toast.error(errMsg(e, restore ? 'Restore failed' : 'Hide failed'));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2 rounded-[12px] border border-black/[0.06] bg-surface p-3 shadow-card">
        <span className="text-[11.5px] text-muted">
          Hide or restore individual products below. Topping a live store up to the current
          template grid needs the fleet rollout service and is not wired here yet.
        </span>
        {!live && <span className="text-[11px] text-muted">Catalog actions run on live stores only.</span>}
      </div>

      {grouped.length === 0 ? (
        <p className="rounded-[14px] border border-dashed border-border p-8 text-center text-[12px] text-muted">
          No products recorded on this store yet.
        </p>
      ) : (
        grouped.map(([garment, rows]) => (
          <section key={garment} className="rounded-[14px] border border-black/[0.05] bg-surface p-4 shadow-card">
            <h3 className="mb-2 text-[12px] font-semibold uppercase tracking-[0.04em] text-muted">
              {garment} <span className="font-normal">· {rows.length}</span>
            </h3>
            <div className="divide-y divide-divider">
              {rows.map((p) => (
                <div key={p.shopifyProductId || p.productConfigId} className="flex items-center gap-3 py-2">
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[12.5px]">{p.title || p.productConfigId || 'Product'}</p>
                    <p className="font-mono text-[10px] text-subtle">{p.productConfigId}{p.designId ? ` · ${p.designId}` : ''}</p>
                  </div>
                  {p.archived && <Badge className="bg-bg text-muted">hidden</Badge>}
                  {live && (p.shopifyGid || p.shopifyProductId) && (
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={!!busy}
                      onClick={() => hideOrRestore(p, !!p.archived)}
                    >
                      {p.archived ? 'Restore' : 'Hide'}
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </section>
        ))
      )}
    </div>
  );
}

function Artwork({ store, refresh }: { store: TeamStoreDetail; refresh: () => Promise<void> }) {
  const [busy, setBusy] = useState<string | null>(null);
  const logos = store.logos ?? [];

  async function overridePreflight() {
    const reason = prompt('Override reason (recorded on the store):');
    if (reason === null) return;
    if (!reason.trim()) {
      toast.error('A reason is required to override preflight');
      return;
    }
    setBusy('override');
    try {
      await post(`/api/team-stores/${store.id}/override-preflight`, { reason: reason.trim() });
      toast.success('Override applied');
      await refresh();
    } catch (e) {
      toast.error(errMsg(e, 'Override failed'));
    } finally {
      setBusy(null);
    }
  }

  async function sendProof() {
    const resend = PROOF_RESEND.has(store.status || '');
    const who = store.adminEmail || store.submittedBy || 'the store admin';
    if (!confirm(`${resend ? 'Resend' : 'Send'} the proof for ${store.schoolName || store.fundraiserName || store.id} to ${who}${store.salesRepEmail ? ` (cc ${store.salesRepEmail})` : ''}? No products are created until they approve it.`)) return;
    setBusy('proof');
    try {
      const r = await post<{ message?: string }>(`/api/team-stores/${store.id}/proof`, {});
      toast.success(r.message || 'Proof render started');
      await refresh();
    } catch (e) {
      toast.error(errMsg(e, 'Send proof failed'));
    } finally {
      setBusy(null);
    }
  }

  async function openProofView() {
    setBusy('proof-view');
    try {
      const r = await get<{ url: string; hasTokenHash: boolean }>(`/api/team-stores/${store.id}/proof-view`);
      window.open(r.url, '_blank', 'noopener');
    } catch (e) {
      toast.error(errMsg(e, 'Could not build the proof link'));
    } finally {
      setBusy(null);
    }
  }

  async function approve(suppressStoreReadyEmail = false) {
    const who = store.adminEmail || store.submittedBy || 'the store admin';
    const tail = suppressStoreReadyEmail
      ? ' No "store is live" email will be sent — you share the link.'
      : ` When it goes live, the store link is emailed to ${who}${store.salesRepEmail ? ` (cc ${store.salesRepEmail})` : ''}.`;
    if (!confirm(`Approve ${store.schoolName || store.fundraiserName || store.id} and create Shopify products?${tail}`)) return;
    setBusy('approve');
    try {
      const r = await post<{ message?: string }>(`/api/team-stores/${store.id}/approve`, { suppressStoreReadyEmail });
      toast.success(r.message || 'Approved — products are being created');
      await refresh();
    } catch (e) {
      toast.error(errMsg(e, 'Approve failed'));
    } finally {
      setBusy(null);
    }
  }

  // Packs by default, or pilot (only stores set Optional/Required get packs)?
  // Unknown while loading: the select waits. A failed read falls back to the
  // pilot reading; the PUT never depends on it.
  const { data: packsSwitch, error: packsSwitchError } = useSWR<{ exists: boolean; byDefault: boolean }>(
    '/api/player-packs/switch',
    get,
    { shouldRetryOnError: false },
  );
  const packsSwitchLoading = !packsSwitch && !packsSwitchError;
  const packs = packsSelectState(store, packsSwitch?.byDefault);
  const packsLocked = !PACKS_EDITABLE.has(store.status || '') || packsSwitchLoading;
  async function savePacks(choice: string) {
    setBusy('packs');
    try {
      await put(`/api/team-stores/${store.id}/player-packs`, { choice });
      toast.success(choice === 'off' ? 'Player Packs off for this store' : `Player Packs ${choice}`);
      await refresh();
    } catch (e) {
      toast.error(errMsg(e, 'Could not save Player Packs'));
    } finally {
      setBusy(null);
    }
  }

  // What the store sells. Hidden until the controls switch is on: the theme that
  // honors the mode has to be pushed first.
  const { data: modeControls } = useSWR<{ enabled: boolean }>('/api/personalization/mode-controls', get, { shouldRetryOnError: false });
  const persMode = modeSelectState(store);
  // The select already shows the saved mode, so choosing it again fires no change.
  const [modeSyncFailed, setModeSyncFailed] = useState(false);
  async function saveMode(mode: string) {
    setBusy('personalization-mode');
    try {
      const res = await put<ModeSaveResult>(`/api/team-stores/${store.id}/personalization-mode`, { mode });
      const stale = storefrontNotUpdated(res);
      setModeSyncFailed(stale);
      if (stale) toast.error('Saved on the store, but the storefront was not updated. Use Retry storefront sync.');
      else toast.success(res.message || 'Personalization saved');
      await refresh();
    } catch (e) {
      toast.error(errMsg(e, 'Could not save personalization'));
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="space-y-3">
      <LiveArtBanner store={store} refresh={refresh} />
      {logos.length === 0 ? (
        <p className="rounded-[14px] border border-dashed border-border p-8 text-center text-[12px] text-muted">
          No logos on this store.
        </p>
      ) : (
        logos.map((logo, idx) => (
          <LogoCard key={logo.id ?? logo.logoId ?? idx} store={store} logo={logo} refresh={refresh} />
        ))
      )}
      <label className="flex items-center gap-2 text-[12px]">
        Player Packs
        <select value={packs.value} disabled={packsLocked || !!busy} onChange={(e) => savePacks(e.target.value)} className={SELECT_CLASS}>
          <option value="off">{packs.offLabel}</option>
          <option value="optional" disabled={packs.needsPersonalization}>Optional</option>
          <option value="required" disabled={packs.needsPersonalization}>Required</option>
        </select>
        <span className="text-muted">
          {packs.needsPersonalization
            ? 'Turn on personalization first.'
            : packs.pilot
              ? 'Pilot: only stores set to Optional or Required get packs. Set before sending the proof.'
              : 'Set before sending the proof so the coach sees the packs.'}
        </span>
      </label>
      {modeControls?.enabled === true && (
        <label className="flex items-center gap-2 text-[12px]">
          Personalization
          <select value={persMode.value} disabled={persMode.locked || !!busy} onChange={(e) => saveMode(e.target.value)} className={SELECT_CLASS}>
            {PERSONALIZATION_MODE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value} disabled={o.value === 'off' && persMode.offDisabled}>{o.label}</option>
            ))}
          </select>
          <span className="text-muted">{persMode.hint}</span>
        </label>
      )}
      {modeControls?.enabled === true && modeSyncFailed && (
        <Button size="sm" variant="secondary" disabled={!!busy} onClick={() => saveMode(persMode.value)}>
          Retry storefront sync
        </Button>
      )}
      <div className="flex flex-wrap gap-2">
        {OVERRIDABLE.has(store.status || '') && (
          <Button size="sm" variant="secondary" disabled={!!busy} onClick={overridePreflight}>
            <ShieldAlert className="h-3.5 w-3.5" aria-hidden /> Override preflight
          </Button>
        )}
        {(PROOF_SENDABLE.has(store.status || '') || PROOF_RESEND.has(store.status || '')) && (
          <Button size="sm" variant="secondary" disabled={!!busy} onClick={sendProof}>
            <Send className="h-3.5 w-3.5" aria-hidden /> {busy === 'proof' ? 'Sending…' : PROOF_RESEND.has(store.status || '') ? 'Resend proof' : 'Send proof'}
          </Button>
        )}
        {PROOF_VIEWABLE.has(store.status || '') && (
          <Button size="sm" variant="secondary" disabled={!!busy} onClick={openProofView}>
            <ExternalLink className="h-3.5 w-3.5" aria-hidden /> View proof page
          </Button>
        )}
        {['pending_review', 'artwork_ready', 'artwork_failed', 'proof_sent', 'changes_requested'].includes(store.status || '') && (
          <>
            <Button size="sm" disabled={!!busy} onClick={() => approve(false)}>
              <CircleCheck className="h-3.5 w-3.5" aria-hidden /> {busy === 'approve' ? 'Working…' : 'Approve & create products'}
            </Button>
            <Button size="sm" variant="secondary" disabled={!!busy} onClick={() => approve(true)} title="Builds the store but sends no automatic store-is-live email">
              <CircleCheck className="h-3.5 w-3.5" aria-hidden /> Approve, I'll send the link
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

type StoreTemplateRow = TemplateLike & {
  id: string;
  styles?: { styleNumber?: string }[];
};

const TEMPLATE_LOCKED = new Set(['creating_products', 'live', 'live_with_errors']);

// Sport + gender are the store's IDENTITY: the sport_id derived from them is
// what the v4 store URL and the Shopify collection are built from, so changing
// them once products exist would point a live collection at another program.
// Mirrors validateStoreTemplatesPatch's identity_locked (409) on the server
// (ghostfit-console/lib/teamstore-wizard.js) — statuses OR any collection gid,
// because a gid can land before the status catches up.
const IDENTITY_LOCKED = new Set([
  'live', 'live_with_errors', 'creating_products', 'partial_create_failure',
  'shopify_error', 'closed', 'archived', 'superseded',
]);

function identityLocked(store: TeamStoreDetail): boolean {
  if (IDENTITY_LOCKED.has(store.status || '')) return true;
  return !!(store.shopify_sport_collection_gid || store.shopifyCollectionGid
    || store.shopifyCollectionId || store.shopify_collection_gid);
}

function TemplateTab({ store, refresh }: { store: TeamStoreDetail; refresh: () => Promise<void> }) {
  const { data: templatesData } = useSWR<{ templates: StoreTemplateRow[] }>('/api/store-templates', get);
  const { data: framesData } = useSWR<{ templates: { id: string; name?: string }[] }>('/api/sport-templates', get, {
    shouldRetryOnError: false,
  });
  const templates = templatesData?.templates || [];
  const productTemplates = sortProductTemplates(templates);
  const allArtwork = useMemo(
    () => sortArtworkTemplates(templates).sort((a, b) => {
      const ag = isLogoOnlyArtwork(a.id || '') ? 0 : 1;
      const bg = isLogoOnlyArtwork(b.id || '') ? 0 : 1;
      return ag - bg || String(a.name || a.id).localeCompare(String(b.name || b.id));
    }),
    [templates],
  );
  const frames = framesData?.templates || [];
  const locked = TEMPLATE_LOCKED.has(store.status || '');
  const idLocked = identityLocked(store);
  const nameOf = (id: string) => templates.find((t) => t.id === id)?.name
    || frames.find((t) => t.id === id)?.name
    || id;

  const savedProduct = currentProductSelection(store);
  const savedArtwork = currentArtworkSelection(store);
  const savedFrame = currentFrameSelection(store);
  const savedSport = store.sportType || store.sport_id || '';
  const savedGender = resolveStoreGender(store) || '';

  const [productId, setProductId] = useState(savedProduct);
  const [artworkId, setArtworkId] = useState(savedArtwork);
  const [frameId, setFrameId] = useState(savedFrame);
  const [sport, setSport] = useState(savedSport);
  const [gender, setGender] = useState(savedGender);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setProductId(savedProduct);
    setArtworkId(savedArtwork);
    setFrameId(savedFrame);
    setSport(savedSport);
    setGender(savedGender);
  }, [savedProduct, savedArtwork, savedFrame, savedSport, savedGender]);

  const artworkChoices = artworkOptionsForProduct(productId, allArtwork, savedArtwork);
  const productTpl = templates.find((t) => t.id === productId);
  const artworkTpl = templates.find((t) => t.id === artworkId);
  const coverage = lineupCoverage(productTpl?.styles || [], artworkTpl?.styles || []);
  const derivedArtwork = sportToArtworkTemplateId(sport);
  const dirty = productId !== savedProduct
    || artworkId !== savedArtwork
    || frameId !== savedFrame
    || sport !== savedSport
    || gender !== savedGender;

  function onProductChange(next: string) {
    setProductId(next);
    if (!artworkAllowedForProduct(next, artworkId)) {
      setArtworkId(suggestArtworkForProduct(next, allArtwork));
    }
  }

  async function saveTemplates() {
    setBusy(true);
    try {
      const patch = templatesPatchFromSelection({
        productTemplateId: productId,
        artworkTemplateId: artworkId,
        sportType: sport,
        gender,
        sportTemplateId: frameId,
      });
      const url = `/api/team-stores/${store.id}/templates`;
      let r: { message?: string; success?: boolean };
      try {
        r = await post<{ message?: string; success?: boolean }>(url, patch);
      } catch (e) {
        // Store-status policy: an artwork-template change on a store that is
        // SELLING (e.g. partial_create_failure) is a FULL rebuild — the store
        // goes offline and needs re-approval. The server refuses until the
        // operator has read that warning; show it, and only on OK resend with
        // confirmRebuild. Cancel changes nothing.
        const body = e instanceof ApiError ? (e.body as { error?: string; message?: string } | null) : null;
        if (!(e instanceof ApiError && e.status === 409 && body?.error === 'rebuild_confirm_required')) throw e;
        if (!window.confirm(body.message || 'This store will go offline and need re-approval. Continue?')) {
          toast.message('Not changed — the store stays live.');
          return;
        }
        r = await post<{ message?: string; success?: boolean }>(url, { ...patch, confirmRebuild: true });
      }
      if (r.success === false) toast.warning(r.message || 'Templates saved, but not everything finished');
      else toast.success(r.message || 'Templates saved');
      await refresh();
    } catch (e) {
      toast.error(errMsg(e, 'Could not save templates'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <div className="rounded-[14px] border border-black/[0.05] bg-surface p-5 shadow-card">
        <div className="mb-3 flex items-start justify-between gap-3">
          <div>
            <h3 className="text-[13px] font-semibold tracking-tight">Templates on this store</h3>
            <p className="mt-1 text-[11.5px] text-muted">
              Lineup, artwork, sport, and composite frame. Saving updates the next product create — live Shopify products are not rewritten.
            </p>
          </div>
          <div className="flex shrink-0 flex-col items-end gap-1">
            <Link to={productLineupHref({ templateId: productId })} className="text-[11px] text-primary underline">
              Edit garments →
            </Link>
            {artworkId && artworkId !== 'logo-only' && !isLogoOnlyArtwork(artworkId) && (
              <Link to={templateEditorHref({ templateId: artworkId })} className="text-[11px] text-primary underline">
                Edit artwork →
              </Link>
            )}
          </div>
        </div>

        {locked && (
          <p className="mb-3 rounded-[10px] bg-warning/10 px-3 py-2 text-[11.5px] text-warning">
            Products already exist on this store. Rebuild it to change templates.
          </p>
        )}

        <dl className="space-y-3 text-[12px]">
          <div>
            <div className="mb-1.5 flex justify-between gap-3">
              <dt className="text-muted">Product template</dt>
              <dd className="text-right font-medium">{nameOf(savedProduct)}</dd>
            </div>
            <select
              value={productId}
              disabled={locked || busy}
              onChange={(e) => onProductChange(e.target.value)}
              className={SELECT_CLASS}
            >
              {savedProduct && !productTemplates.some((t) => t.id === savedProduct) && (
                <option value={savedProduct}>{savedProduct} (current — legacy)</option>
              )}
              {productTemplates.map((t) => (
                <option key={t.id} value={t.id || ''}>
                  {t.name || t.id}{t.styles?.length ? ` — ${t.styles.length} styles` : ''}
                </option>
              ))}
            </select>
          </div>

          <div>
            <div className="mb-1.5 flex justify-between gap-3">
              <dt className="text-muted">Artwork template</dt>
              <dd className="text-right font-medium">{savedArtwork === 'logo-only' ? 'Logo only' : nameOf(savedArtwork)}</dd>
            </div>
            <select
              value={artworkId}
              disabled={locked || busy}
              onChange={(e) => setArtworkId(e.target.value)}
              className={SELECT_CLASS}
            >
              <option value="logo-only">Logo only (no designs)</option>
              {artworkId !== 'logo-only' && !artworkChoices.some((t) => t.id === artworkId) && (
                <option value={artworkId}>{nameOf(artworkId)}</option>
              )}
              {artworkChoices.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name || t.id}{isLogoOnlyArtwork(t.id || '') ? ' — school logo only' : ''}
                </option>
              ))}
            </select>
            {derivedArtwork && derivedArtwork !== artworkId && artworkAllowedForProduct(productId, derivedArtwork) && (
              <button
                type="button"
                disabled={locked || busy}
                onClick={() => setArtworkId(derivedArtwork)}
                className="mt-1.5 text-[11px] text-primary underline"
              >
                Use {nameOf(derivedArtwork)} from sport
              </button>
            )}
            <p className={`mt-1.5 text-[11px] ${coverage.total > 0 && artworkTpl && !isLogoOnlyArtwork(artworkId) && coverage.covered < coverage.total ? 'text-warning' : 'text-muted'}`}>
              {isLogoOnlyArtwork(artworkId) || artworkId === 'logo-only'
                ? 'General / logo-only builds every garment with the school logo — no AskBruce designs.'
                : artworkTpl
                  ? `${nameOf(artworkId)} covers ${coverage.covered} of ${coverage.total || 0} garments on ${nameOf(productId)}${coverage.missing.length ? ` — missing ${coverage.missing.join(', ')}` : ''}. Uncovered styles are dropped from the store.`
                  : 'Pick artwork to see garment coverage.'}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <dt className="mb-1.5 text-muted">Sport</dt>
              <input
                value={sport}
                disabled={idLocked || busy}
                onChange={(e) => setSport(e.target.value)}
                className={SELECT_CLASS}
                placeholder="volleyball"
              />
            </div>
            <div>
              <dt className="mb-1.5 text-muted">Gender</dt>
              <select
                value={gender}
                disabled={idLocked || busy}
                onChange={(e) => setGender(e.target.value)}
                className={SELECT_CLASS}
              >
                <option value="">—</option>
                {STORE_GENDERS.map((g) => (
                  <option key={g} value={g}>{genderLabel(g)}</option>
                ))}
              </select>
            </div>
            {idLocked && (
              <p className="col-span-2 text-[11px] text-muted">
                Sport and gender fix the store URL and cannot change after products exist. Create a new store for the other sport.
              </p>
            )}
          </div>

          {frames.length > 0 && (
            <div>
              <dt className="mb-1.5 text-muted">Composite frame</dt>
              <select
                value={frameId}
                disabled={locked || busy}
                onChange={(e) => setFrameId(e.target.value)}
                className={SELECT_CLASS}
              >
                {frameId && !frames.some((t) => t.id === frameId) && (
                  <option value={frameId}>{frameId}</option>
                )}
                {frames.map((t) => (
                  <option key={t.id} value={t.id}>{t.name || t.id}</option>
                ))}
              </select>
              <p className="mt-1.5 text-[11px] text-muted">
                Print-area frames and fonts. Independent of AskBruce designs — leave volleyball frames even when artwork is General.
              </p>
            </div>
          )}
        </dl>

        <Button size="sm" className="mt-4" disabled={locked || busy || !dirty} onClick={() => void saveTemplates()}>
          {busy ? 'Saving…' : 'Save templates'}
        </Button>
      </div>

      <div className="rounded-[14px] border border-dashed border-border p-5">
        <h3 className="mb-1 text-[13px] font-semibold tracking-tight">Push to live stores / Schedule a drop</h3>
        <p className="text-[11.5px] text-muted">
          Fleet template rollout and scheduled drops are not available yet — the
          teamstore-createproducts-v4 endpoints behind them are still on an unmerged branch.
          Nothing here would reach a live store, so the controls are withheld rather than
          shown broken.
        </p>
      </div>
    </div>
  );
}

function orderCustomerLabel(o: NonNullable<OrdersPayload['orders']>[number]): string {
  return [o.customer?.firstName, o.customer?.lastName].filter(Boolean).join(' ') || o.customer?.email || 'Order';
}

function OrdersTab({ store }: { store: TeamStoreDetail }) {
  const { data, isLoading, error } = useSWR<OrdersPayload>(`/api/team-stores/${store.id}/orders`, get, {
    shouldRetryOnError: false,
  });
  const orders = data?.orders || [];
  const stats = data?.orderStats || store.orderStats;

  if (isLoading) return <Skeleton className="h-32 w-full rounded-[14px]" />;
  if (error || !data) {
    return (
      <div className="rounded-[14px] border border-dashed border-border p-8 text-center text-[12px] text-muted">
        Team store orders are not available from this page yet.
        <div className="mt-2">
          <Link to="/orders" className="text-primary underline-offset-2 hover:underline">Open Order Tracking →</Link>
        </div>
      </div>
    );
  }
  if (orders.length === 0) {
    return (
      <div className="rounded-[14px] border border-dashed border-border p-8 text-center text-[12px] text-muted">
        No teamStoreOrders for this store.
        {stats?.totalOrders ? ` Lifetime stats still show ${stats.totalOrders} order(s).` : ''}
        <div className="mt-2">
          <Link to="/orders" className="text-primary underline-offset-2 hover:underline">Open Order Tracking →</Link>
        </div>
      </div>
    );
  }
  return (
    <div className="overflow-hidden rounded-[12px] border border-black/[0.05] bg-surface shadow-card">
      {orders.map((o) => (
        <div key={o.id} className="flex items-center gap-3 border-b border-divider px-3.5 py-2.5 last:border-b-0">
          <span className="font-mono text-[11px] text-subtle">{o.shopifyOrderId || o.id}</span>
          <span className="flex-1 truncate text-[12.5px]">{orderCustomerLabel(o)}</span>
          <span className="text-[12px]">{o.totalQuantity ?? '—'} u</span>
          <span className="w-20 text-right text-[12px]">{o.subtotal != null ? money(o.subtotal) : '—'}</span>
          <span className="w-16 text-right text-[11px] text-muted">{o.createdAt ? formatRelative(o.createdAt) : ''}</span>
        </div>
      ))}
    </div>
  );
}

type BuyerRow = {
  id: string;
  email?: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  customerName?: string;
  customer?: { email?: string; firstName?: string; lastName?: string };
  createdAt?: string;
  lastOrderAt?: string;
  orderNumber?: string;
  shopifyOrderId?: string;
  lastOrderId?: string;
  orderId?: string;
  order?: string;
};

function buyerName(b: BuyerRow): string {
  const fromParts = [b.firstName, b.lastName].filter(Boolean).join(' ');
  const fromCustomer = [b.customer?.firstName, b.customer?.lastName].filter(Boolean).join(' ');
  return b.name || b.customerName || fromParts || fromCustomer || '—';
}

function buyerEmail(b: BuyerRow): string {
  return b.email || b.customer?.email || '—';
}

function buyerDate(b: BuyerRow): string | undefined {
  return b.createdAt || b.lastOrderAt;
}

function buyerOrder(b: BuyerRow): string {
  return b.orderNumber || b.shopifyOrderId || b.lastOrderId || b.orderId || b.order || '—';
}

function BuyersTab({ store }: { store: TeamStoreDetail }) {
  const { data, isLoading, error } = useSWR<{ buyers?: BuyerRow[] }>(
    `/api/team-stores/${store.id}/buyers`,
    get,
    { shouldRetryOnError: false },
  );
  const buyers = data?.buyers || [];

  if (isLoading) return <Skeleton className="h-32 w-full rounded-[14px]" />;
  if (error) {
    return (
      <div className="space-y-4">
        <div className="rounded-[14px] border border-dashed border-border p-8 text-center text-[12px] text-muted">
          No buyers yet.
        </div>
        <DropEmailForm storeId={store.id} />
      </div>
    );
  }
  if (buyers.length === 0) {
    return (
      <div className="space-y-4">
        <div className="rounded-[14px] border border-dashed border-border p-8 text-center text-[12px] text-muted">
          No buyers yet.
        </div>
        <DropEmailForm storeId={store.id} />
      </div>
    );
  }
  return (
    <div className="space-y-4">
    <div className="overflow-hidden rounded-[12px] border border-black/[0.05] bg-surface shadow-card">
      <div className="flex items-center gap-3 border-b border-divider px-3.5 py-2 text-[10px] font-semibold uppercase tracking-[0.05em] text-subtle">
        <span className="min-w-0 flex-1">Email</span>
        <span className="w-36 shrink-0">Name</span>
        <span className="w-24 shrink-0 text-right">Order</span>
        <span className="w-16 shrink-0 text-right">Date</span>
      </div>
      {buyers.map((b) => (
        <div key={b.id} className="flex items-center gap-3 border-b border-divider px-3.5 py-2.5 last:border-b-0">
          <span className="min-w-0 flex-1 truncate text-[12.5px]">{buyerEmail(b)}</span>
          <span className="w-36 shrink-0 truncate text-[12.5px]">{buyerName(b)}</span>
          <span className="w-24 shrink-0 truncate text-right font-mono text-[11px] text-subtle">{buyerOrder(b)}</span>
          <span className="w-16 shrink-0 text-right text-[11px] text-muted">
            {buyerDate(b) ? formatRelative(buyerDate(b)!) : '—'}
          </span>
        </div>
      ))}
    </div>
      <DropEmailForm storeId={store.id} />
    </div>
  );
}

// The queue that replaces "email Grant a change". Types come from the server
// catalog (lib/store-change-requests.js), which is the taxonomy of what reps
// actually asked for over 180 days of email.
function RequestsTab({ storeId }: { storeId: string }) {
  const { data: catalog } = useSWR<{ types: ChangeRequestType[] }>('/api/team-stores/request-types', get);
  const { data, isLoading, mutate } = useSWR<{ requests: ChangeRequest[] }>(
    `/api/team-stores/${encodeURIComponent(storeId)}/requests`,
    get,
  );

  const types = catalog?.types ?? [];
  const [typeId, setTypeId] = useState('');
  const [notes, setNotes] = useState('');
  const [details, setDetails] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const selected = types.find((t) => t.id === typeId) || null;
  const requests = data?.requests ?? [];
  const openCount = requests.filter((r) => r.status === 'open' || r.status === 'in_progress').length;

  async function submit() {
    setBusy(true);
    setError('');
    try {
      await post(`/api/team-stores/${encodeURIComponent(storeId)}/requests`, { type: typeId, notes, details });
      setTypeId('');
      setNotes('');
      setDetails({});
      await mutate();
    } catch (e) {
      const body = e instanceof ApiError ? (e.body as { errors?: string[]; message?: string }) : null;
      setError(body?.errors?.join(' · ') || body?.message || 'Could not file that request.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-4">
      <div className="rounded-[14px] border border-black/[0.05] bg-surface p-5 shadow-card">
        <h3 className="mb-1 text-[11px] font-semibold uppercase tracking-[0.05em] text-muted">File a change request</h3>
        <p className="mb-4 text-[12px] text-muted">
          Anything a coach or rep would normally email about this store. It lands in the queue below instead of an inbox.
        </p>

        <div className="grid gap-3 sm:grid-cols-2">
          <label className="block">
            <span className="mb-1 block text-[11px] font-medium text-muted">What needs to change?</span>
            <select
              value={typeId}
              onChange={(e) => { setTypeId(e.target.value); setDetails({}); }}
              className="w-full rounded-lg border border-black/[0.08] bg-bg px-3 py-2 text-[13px]"
            >
              <option value="">Choose…</option>
              {types.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
            </select>
          </label>

          {selected?.fields.filter((f) => f !== 'notes').map((f) => (
            <label key={f} className="block">
              <span className="mb-1 block text-[11px] font-medium text-muted">{FIELD_LABELS[f] || f}</span>
              <input
                type={f === 'startDate' || f === 'endDate' ? 'date' : 'text'}
                value={details[f] || ''}
                onChange={(e) => setDetails((d) => ({ ...d, [f]: e.target.value }))}
                className="w-full rounded-lg border border-black/[0.08] bg-bg px-3 py-2 text-[13px]"
              />
            </label>
          ))}
        </div>

        {selected?.help ? <p className="mt-3 text-[12px] text-muted">{selected.help}</p> : null}
        {selected?.example ? (
          <p className="mt-1 text-[12px] italic text-muted">e.g. &ldquo;{selected.example}&rdquo;</p>
        ) : null}
        {selected?.needsArtwork ? (
          <p className="mt-2 text-[12px] text-warning">Attach or link the artwork in the notes — this cannot be actioned without it.</p>
        ) : null}

        <label className="mt-3 block">
          <span className="mb-1 block text-[11px] font-medium text-muted">Notes</span>
          <textarea
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="What exactly should change, and anything the person asking said."
            className="w-full rounded-lg border border-black/[0.08] bg-bg px-3 py-2 text-[13px]"
          />
        </label>

        {error ? <p className="mt-2 text-[12px] text-danger">{error}</p> : null}

        <div className="mt-3">
          <Button size="sm" disabled={!typeId || busy} onClick={submit}>
            {busy ? 'Filing…' : 'File request'}
          </Button>
        </div>
      </div>

      <div className="rounded-[14px] border border-black/[0.05] bg-surface p-5 shadow-card">
        <h3 className="mb-3 text-[11px] font-semibold uppercase tracking-[0.05em] text-muted">
          Requests {openCount > 0 ? `· ${openCount} open` : ''}
        </h3>
        {isLoading ? (
          <p className="text-[12px] text-muted">Loading…</p>
        ) : (
          <ChangeRequestList storeId={storeId} requests={requests} types={types} onChanged={() => mutate()} />
        )}
      </div>
    </div>
  );
}

function PersonalizationTab({ store, refresh }: { store: TeamStoreDetail; refresh: () => Promise<unknown> }) {
  const { data, isLoading } = useSWR<{ enabled?: boolean; defaulted?: boolean }>(
    '/api/personalization/global-switch',
    get,
    { shouldRetryOnError: false },
  );
  return (
    <div className="space-y-4">
      <StoreNameNumberFontCard store={store} refresh={refresh} />
      <div className="rounded-[14px] border border-black/[0.05] bg-surface p-5 shadow-card">
        <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-[0.05em] text-muted">Shop-wide personalization</h3>
        <p className="text-[13px]">
          The V2 shop flag is still shop-wide. The name/number face above is this store only — it is not a shopper picker and not per-SKU.
        </p>
        {isLoading ? (
          <p className="mt-2 text-[12px] text-muted">Checking the shop-wide V2 flag…</p>
        ) : (
          <p className="mt-2 text-[12px] text-muted">
            Shop-wide V2 is currently <span className="font-semibold text-text">{data?.enabled ? 'ON' : 'OFF'}</span>
            {data?.defaulted ? ' (unset shops default ON)' : ''}.
            Change it on Personalization → Templates, not here.
          </p>
        )}
        <div className="mt-4 flex flex-wrap gap-3">
          <Button asChild size="sm">
            <Link to="/personalization/assign">Assign templates to products</Link>
          </Button>
          <Button asChild size="sm" variant="secondary">
            <Link to="/personalization/templates">Manage templates</Link>
          </Button>
        </div>
      </div>
    </div>
  );
}

type LePayload = {
  storeId?: string;
  source?: string | null;
  leading_edge_event_id?: string | null;
  leading_edge_external_id?: string | null;
  leading_edge_callback_url?: string | null;
  submittedBy?: string | null;
  head_coach_name?: string | null;
  head_coach_email?: string | null;
  roster_count?: number | null;
  event?: {
    event_id?: string;
    type?: string | null;
    callback_url?: string | null;
    last_callback?: { status?: number; payload?: string; sent_at?: string; error?: string } | null;
    proof_status?: string | null;
    payload?: {
      school_name?: string | null;
      sport?: string | null;
      enable_team_store?: boolean | null;
      head_coach_name?: string | null;
      head_coach_email?: string | null;
      roster_count?: number | null;
    } | null;
  } | null;
};

function LeadingEdgeTab({ store }: { store: TeamStoreDetail }) {
  const { data, isLoading, error } = useSWR<LePayload>(
    `/api/team-stores/${store.id}/leading-edge`,
    get,
    { shouldRetryOnError: false },
  );
  if (isLoading) return <Skeleton className="h-32 w-full rounded-[14px]" />;
  if (error || !data) {
    return (
      <div className="rounded-[14px] border border-dashed border-border p-8 text-center text-[12px] text-muted">
        Leading Edge details are not available for this store.
      </div>
    );
  }
  if (!data.leading_edge_event_id && data.source !== 'leading_edge') {
    return (
      <div className="rounded-[14px] border border-dashed border-border p-8 text-center text-[12px] text-muted">
        This store was not created from a Leading Edge event.
      </div>
    );
  }
  const ev = data.event;
  const cb = ev?.last_callback;
  return (
    <div className="rounded-[14px] border border-black/[0.05] bg-surface p-5 shadow-card">
      <h3 className="mb-3 text-[11px] font-semibold uppercase tracking-[0.05em] text-muted">What LE sent / what we sent back</h3>
      <dl className="grid grid-cols-2 gap-x-6 gap-y-3">
        <Row label="Event id"><span className="font-mono text-[11.5px]">{data.leading_edge_event_id || '—'}</span></Row>
        <Row label="External id"><span className="font-mono text-[11.5px]">{data.leading_edge_external_id || ev?.payload?.school_name || '—'}</span></Row>
        <Row label="Coach">{data.head_coach_name || ev?.payload?.head_coach_name || '—'}</Row>
        <Row label="Coach email">{data.head_coach_email || ev?.payload?.head_coach_email || '—'}</Row>
        <Row label="Roster">{data.roster_count ?? ev?.payload?.roster_count ?? '—'}</Row>
        <Row label="Callback">{data.leading_edge_callback_url || ev?.callback_url || '—'}</Row>
        <Row label="Last callback">
          {cb
            ? `${cb.payload || '—'} · ${cb.status ?? cb.error ?? ''} ${cb.sent_at ? formatRelative(cb.sent_at) : ''}`
            : 'None recorded'}
        </Row>
        <Row label="Proof status">{ev?.proof_status || '—'}</Row>
      </dl>
    </div>
  );
}

function PayoutsTab({ store }: { store: TeamStoreDetail }) {
  const { data, isLoading, error } = useSWR<{ payouts?: PayoutRow[] }>(
    `/api/team-stores/${store.id}/payouts`,
    get,
    { shouldRetryOnError: false },
  );
  const payouts = data?.payouts || [];

  if (isLoading) return <Skeleton className="h-32 w-full rounded-[14px]" />;
  if (error) {
    return (
      <div className="rounded-[14px] border border-dashed border-border p-8 text-center text-[12px] text-danger">
        Couldn’t load payouts for this store. Firestore did not return statements.
        <div className="mt-2">
          <Link to="/payouts" className="text-primary underline-offset-2 hover:underline">All payouts →</Link>
        </div>
      </div>
    );
  }
  if (payouts.length === 0) {
    return (
      <div className="rounded-[14px] border border-dashed border-border p-8 text-center text-[12px] text-muted">
        No quarterly statements for this store yet. teamStorePayouts lights up after the payout job (PR 357) merges and runs.
        <div className="mt-2">
          <Link to="/payouts" className="text-primary underline-offset-2 hover:underline">All payouts →</Link>
        </div>
      </div>
    );
  }
  return (
    <div>
      <PayoutTable payouts={payouts} showStore={false} />
      <p className="mt-3 text-[11px] text-muted">
        Team 10% / rep 7% of that quarter’s net revenue. Store stays open.{' '}
        <Link to={`/payouts?store=${encodeURIComponent(store.id)}`} className="text-primary underline-offset-2 hover:underline">
          Open in Payouts →
        </Link>
      </p>
    </div>
  );
}
